import React from 'react';
import { Clock, ArrowRight, MapPin, Info } from 'lucide-react';

/**
 * Компонент для расчета и отображения разницы во времени между городами
 */
const TimeDifferenceCalculator = ({ 
  formData, 
  timeDifference,
  russianCities,
  worldCities
}) => {
  if (!timeDifference) {
    return null;
  }

  const {
    hours,
    minutes,
    sign,
    formattedDifference,
    sourceTime,
    targetTime,
    sourceCityInfo,
    targetCityInfo,
    exampleTimes
  } = timeDifference;

  const sourceCityName = sourceCityInfo ? sourceCityInfo.name : formData.sourceCity;
  const targetCityName = targetCityInfo ? targetCityInfo.name : formData.targetCity;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <Clock className="w-5 h-5 mr-2 text-purple-500" />
        Разница во времени между городами
      </h2>

      {/* Города и разница во времени */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between">
          <div className="mb-4 sm:mb-0 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center sm:flex-1">
            <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">{sourceCityName}</h3>
            <p className="text-3xl font-bold text-purple-800 dark:text-purple-200">{sourceTime}</p>
            <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
              UTC{sourceCityInfo?.utcOffset || ""}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center px-4 py-2 mb-4 sm:mb-0">
            <ArrowRight className="w-6 h-6 text-gray-400 dark:text-gray-500 mb-2" />
            <div className="text-lg font-semibold text-gray-700 dark:text-gray-300">{formattedDifference}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400">разница</p>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg text-center sm:flex-1">
            <h3 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-1">{targetCityName}</h3>
            <p className="text-3xl font-bold text-indigo-800 dark:text-indigo-200">{targetTime}</p>
            <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
              UTC{targetCityInfo?.utcOffset || ""}
            </p>
          </div>
        </div>
      </div>

      {/* Пояснение разницы */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 mb-6">
        <div className="flex">
          <Info className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-blue-700 dark:text-blue-300 font-medium text-sm mb-1">Интерпретация разницы во времени</h3>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              {sign === '+' 
                ? `В городе ${targetCityName} на ${hours} ${formatHours(hours)} ${minutes > 0 ? `${minutes} ${formatMinutes(minutes)}` : ''} впереди, чем в ${sourceCityName}.` 
                : `В городе ${targetCityName} на ${hours} ${formatHours(hours)} ${minutes > 0 ? `${minutes} ${formatMinutes(minutes)}` : ''} позади, чем в ${sourceCityName}.`
              }
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
              {sign === '+' 
                ? `Когда в ${sourceCityName} полдень, в ${targetCityName} уже ${12 + hours}:${minutes === 0 ? '00' : minutes}.` 
                : `Когда в ${sourceCityName} полдень, в ${targetCityName} только ${hours > 12 ? 24 - hours : 12 - hours}:${minutes === 0 ? '00' : (60 - minutes) % 60}.`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Таблица соответствия времени */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <Clock className="w-4 h-4 mr-2 text-purple-500" />
          Таблица соответствия времени
        </h3>
        
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {sourceCityName}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {targetCityName}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {exampleTimes.map((time, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/30' : ''}>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">
                    {time.sourceTime}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {time.targetTime}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Советы для путешественников */}
      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <MapPin className="w-4 h-4 mr-2 text-purple-500" />
          Советы при путешествии из {sourceCityName} в {targetCityName}
        </h3>
        
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          {sign === '+' && hours >= 3 ? (
            <p>• При перелёте на восток (с увеличением времени) может возникнуть <strong>джетлаг</strong>. Рекомендуется немного подстроить режим до вылета.</p>
          ) : sign === '-' && hours >= 3 ? (
            <p>• При перелёте на запад (с уменьшением времени) джетлаг обычно переносится легче, но может возникнуть дневная сонливость.</p>
          ) : null}
          
          <p>• Разница составляет {formattedDifference}, учитывайте это при планировании звонков, встреч и дел.</p>
          
          {hours >= 4 ? (
            <p>• Значительная разница во времени может потребовать 1-2 дня на акклиматизацию.</p>
          ) : null}
          
          <p>• Используйте местное время в телефоне для синхронизации с новым часовым поясом.</p>
          
          {hasBusinessHoursOverlap(sign, hours) ? (
            <p>• В {sourceCityName} и {targetCityName} есть пересечение рабочих часов, удобное для бизнес-коммуникаций.</p>
          ) : (
            <p>• Обратите внимание, что рабочие часы практически не пересекаются, планируйте коммуникацию заранее.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения часов
const formatHours = (hours) => {
  if (hours % 10 === 1 && hours % 100 !== 11) {
    return 'час';
  } else if ([2, 3, 4].includes(hours % 10) && ![12, 13, 14].includes(hours % 100)) {
    return 'часа';
  } else {
    return 'часов';
  }
};

// Вспомогательная функция для склонения минут
const formatMinutes = (minutes) => {
  if (minutes % 10 === 1 && minutes % 100 !== 11) {
    return 'минуту';
  } else if ([2, 3, 4].includes(minutes % 10) && ![12, 13, 14].includes(minutes % 100)) {
    return 'минуты';
  } else {
    return 'минут';
  }
};

// Функция определения пересечения бизнес-часов
const hasBusinessHoursOverlap = (sign, hours) => {
  // Предполагаем, что рабочие часы с 9 до 18
  // Если разница меньше 9 часов, то есть пересечение
  return hours < 9;
};

export default TimeDifferenceCalculator;